import { handleToastAsyncAction } from '../../utils/handleToastAsyncAction';
import {
  Address,
  DeleteButton,
  DeleteIcon,
  EditButton,
  Item,
  Name,
  StarButton,
  StarIcon,
  Top,
} from './AddressItem.styled';
import {
  ButtonsWrapper,
  CancelButton,
  ModalCloseButton,
  ModalCloseIcon,
  ModalContent,
  ModalExclamationIcon,
  ModalWrapper,
  Title,
} from '../../shared/DeleteConfirmationModal/DeleteConfirmationModal.styled';
import {
  Description,
  Wrapper,
} from '../../components/ProductDetail/ProductDetail.styled';
import { deleteAddress, setPrimaryAddress, retreiveUserAddresses } from '../../api/addresses';
import {
  setPrimary,
  setAddresses,
} from '../../store/addresses/addressesSlice';
import {
  faExclamationCircle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { faStar, faTrashCan } from '@fortawesome/free-solid-svg-icons';

import { Button } from '../../shared';
import { FormEvent } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { UserAdressType } from '../../types/address';
import { faStar as faStarEmpty } from '@fortawesome/free-regular-svg-icons';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import * as Sentry from '@sentry/react';

interface Props extends UserAdressType {
  setOpenEditAddressForm: (open: boolean) => void;
  openEditAddressForm: boolean;
  setAddressToEdit: (item: UserAdressType) => void;
}

export const AddressItem = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const icon = props.primary ? faStar : (faStarEmpty as IconProp);

  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);

  const handleConfirmModal = async () => {
    setConfirmDeleteModal(!confirmDeleteModal);
  };

  const handleDeleteAddress = async (event: FormEvent<HTMLElement>) => {
    event.preventDefault();

    const result = await handleToastAsyncAction(
      () => deleteAddress({ id: props.address.id }),
      {
        onSuccessMessage: t('addresses.delete.toastify.success'),
        onErrorMessage: t('addresses.delete.toastify.error'),
        dispatchAction: async () => {
          try {
            const updatedAddresses = await retreiveUserAddresses();
            dispatch(setAddresses(updatedAddresses));

            const hasPrimary = updatedAddresses.results.some((address: any) => address.primary);

            if (!hasPrimary && updatedAddresses.results.length > 0) {
              const firstAddress = updatedAddresses.results[0];
              try {
                await setPrimaryAddress({ id: firstAddress.id });
                const finalAddresses = await retreiveUserAddresses();
                dispatch(setAddresses(finalAddresses));
              } catch (error) {
                console.error('Error setting first address as primary:', error);
              }
            }
          } catch (error) {
            console.error('Error reloading addresses:', error);
          }
        },
        errorLogger: (error) => {
          console.error('Error deleting address:', error);
          Sentry.captureException(error);
        },
      }
    );

    if (!result.success && result.error?.response?.status === 404) {
      setConfirmDeleteModal(false);
    }
  };

  const handleSetPrimaryAddress = async (event: FormEvent<HTMLElement>) => {
    event.preventDefault();

    if (props.primary) {
      return;
    }

    await handleToastAsyncAction(
      () => setPrimaryAddress({ id: props.id }),
      {
        onSuccessMessage: t('addresses.setPrimary.toastify.success'),
        onErrorMessage: t('addresses.setPrimary.toastify.error'),
        dispatchAction: () => dispatch(setPrimary({ id: props.id })),
        errorLogger: (error) => {
          console.error('Error setting primary address:', error);
          Sentry.captureException(error, {
            tags: {
              action: 'set_primary_address',
              addressId: props.id
            }
          });
        },
      }
    );
  };

  const handleEditAddress = (event: FormEvent<HTMLElement>) => {
    event.preventDefault();
    props.setOpenEditAddressForm(!props.openEditAddressForm);
    props.setAddressToEdit({
      id: props.id,
      name: props.name,
      primary: props.primary,
      address: props.address,
    });
  };

  return (
    <>
      <Item>
        <Top>
          <StarButton onClick={handleSetPrimaryAddress}>
            <StarIcon icon={icon} width={23} $primary={!!props.primary} />
          </StarButton>
          <Name>{props.name || 'My Address'}</Name>
          <EditButton onClick={handleEditAddress}>
            {t('shared.action.edit')}
          </EditButton>
        </Top>
        <Address>{props.address.address}</Address>
        <DeleteButton onClick={handleConfirmModal}>
          <DeleteIcon icon={faTrashCan} width={14} />
        </DeleteButton>
        {confirmDeleteModal ? (
          <ModalWrapper>
            <ModalContent>
              <ModalCloseButton onClick={handleConfirmModal}>
                <ModalCloseIcon icon={faTimes} />
              </ModalCloseButton>
              <Wrapper>
                <ModalExclamationIcon icon={faExclamationCircle} />
                <Title>{t('addresses.deleteModal.title')}</Title>
                <Description>
                  {t('addresses.deleteModal.description')}
                </Description>
                <ButtonsWrapper>
                  <Button
                    title={t('addresses.deleteModal.cta.delete')}
                    onClick={handleDeleteAddress}
                  />
                  <CancelButton
                    title={t('addresses.deleteModal.cta.cancel')}
                    type="reset"
                    onClick={() => setConfirmDeleteModal(false)}
                  />
                </ButtonsWrapper>
              </Wrapper>
            </ModalContent>
          </ModalWrapper>
        ) : null}
      </Item>
    </>
  );
};
